import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Avatar,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useEffect, useState } from "react";
import { uploadImage } from "./uploadImage";

export interface Field<T> {
  key: keyof T;
  label: string;
  type?: "text" | "number" | "currency" | "color" | "image" | "select" | "boolean";
  options?: { value: string; label: string }[];
}

interface Props<T extends Record<string, any>> {
  open: boolean;
  onClose: () => void;
  initial: T;
  fields: Field<T>[];
  onSubmit: (values: T) => Promise<void>;
}

export default function ItemDialog<T extends Record<string, any>>({
  open,
  onClose,
  initial,
  fields,
  onSubmit,
}: Props<T>) {
  const [values, setValues] = useState<T>(initial);
  const [displayInputs, setDisplayInputs] = useState<Record<string, string>>({});
  const [loadingImage, setLoadingImage] = useState(false);

  useEffect(() => {
    setValues(initial);
    setDisplayInputs({});
  }, [initial, open]);

  const handleChange = (key: keyof T, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {initial?.id ? "Editar" : "Nuevo registro"}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          {fields.map((field) => {
            /* ---------- IMAGE / BANNER ---------- */
            if (field.type === "image") {
              return (
                <Stack key={String(field.key)} spacing={1}>
                  <Typography variant="subtitle2">
                    {field.label}
                  </Typography>

                  <Button
                    variant="outlined"
                    component="label"
                    disabled={loadingImage}
                  >
                    {loadingImage ? "Subiendo..." : "Subir banner"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        setLoadingImage(true);
                        const url = await uploadImage(
                          file,
                          `banners/${Date.now()}-${file.name}`
                        );
                        handleChange(field.key, url);
                        setLoadingImage(false);
                      }}
                    />
                  </Button>

                  {values[field.key] && (
                    <Avatar
                      src={values[field.key]}
                      variant="rounded"
                      sx={{
                        width: "100%",
                        height: 140,
                        borderRadius: 2,
                      }}
                    />
                  )}
                </Stack>
              );
            }

            /* ---------- SELECT INPUT ---------- */
            if (field.type === "select") {
              return (
                <TextField
                  key={String(field.key)}
                  select
                  label={field.label}
                  value={values[field.key] ?? ""}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  fullWidth
                >
                  {field.options?.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }

            /* ---------- BOOLEAN / SWITCH ---------- */
            if (field.type === "boolean") {
              return (
                <FormControlLabel
                  key={String(field.key)}
                  control={
                    <Switch
                      checked={Boolean(values[field.key])}
                      onChange={(e) => handleChange(field.key, e.target.checked)}
                      color="primary"
                    />
                  }
                  label={field.label}
                />
              );
            }

            /* ---------- CURRENCY INPUT (CON PUNTOS DE MILES 1.000 / 10.000) ---------- */
            if (field.type === "currency") {
              const rawVal = values[field.key];
              let displayValue = "";

              if (displayInputs[String(field.key)] !== undefined) {
                displayValue = displayInputs[String(field.key)];
              } else if (rawVal !== undefined && rawVal !== null && rawVal !== 0) {
                const num = Number(rawVal);
                const isNeg = num < 0;
                displayValue = (isNeg ? "-" : "") + Math.abs(num).toLocaleString("es-CO");
              } else if (initial?.id && rawVal === 0) {
                displayValue = "0";
              } else {
                displayValue = "";
              }

              return (
                <TextField
                  key={String(field.key)}
                  label={field.label}
                  type="text"
                  value={displayValue}
                  placeholder="Ej: 1.000"
                  onChange={(e) => {
                    const inputStr = e.target.value;
                    const isNegative = inputStr.startsWith("-");
                    const cleanDigits = inputStr.replace(/\D/g, "");

                    let newDisplay = "";
                    let newNum = 0;

                    if (cleanDigits) {
                      const parsed = Number(cleanDigits);
                      newNum = isNegative ? -parsed : parsed;
                      newDisplay = (isNegative ? "-" : "") + parsed.toLocaleString("es-CO");
                    } else if (isNegative) {
                      newDisplay = "-";
                      newNum = 0;
                    } else {
                      newDisplay = "";
                      newNum = 0;
                    }

                    setDisplayInputs((prev) => ({ ...prev, [String(field.key)]: newDisplay }));
                    handleChange(field.key, newNum);
                  }}
                  fullWidth
                />
              );
            }

            /* ---------- NORMAL INPUT ---------- */
            const isNumberField = field.type === "number";
            const val = values[field.key];
            const displayVal = (isNumberField && val === 0 && !initial?.id) ? "" : (val ?? "");

            return (
              <TextField
                key={String(field.key)}
                label={field.label}
                type={field.type || "text"}
                value={displayVal}
                onChange={(e) =>
                  handleChange(
                    field.key,
                    field.type === "number"
                      ? (e.target.value === "" ? 0 : Number(e.target.value))
                      : e.target.value
                  )
                }
                fullWidth
              />
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(values)}
          disabled={loadingImage}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
